// TypeScript port of the TermMax pricing primitives we need for display and
// for the on-chain fallback. Source: term-structure/termmax-contract-v2 @ main,
// `contracts/v1/lib/TermMaxCurve.sol` + `TermMaxOrderV2.apr()`.
//
// Kept dependency-free (like `midnight/math.ts`) so margin-fetcher owns the
// math. Covered by math.test.ts.
//
// SCOPE: this file implements the MARGINAL (mid) rate plus the fee/LTV
// helpers — enough to display a rate and to fall back on-chain. It does NOT
// implement the full curve WALK (`cutsForwardIter` / `cutsReverseIter`), which
// is what an executable quote needs. Executable quotes come from the TermMax
// aggregator endpoint; porting the walk is a later step and must be validated
// bit-exact against `swapExactTokenToToken` on a fork first — the ceil-day and
// virtual-reserve traps below will otherwise drift silently.

import type { TermMaxCurveSegment, TermMaxFeeConfig } from './types'

/** TermMax ratio base: `0.01e8` = 1%. */
export const DECIMAL_BASE = 100_000_000n // 1e8
export const DAYS_IN_YEAR = 365n
export const SECONDS_IN_DAY = 86_400n

/** Max fee ratio the protocol will accept on a market (`0.2e8` = 20%). */
export const MAX_FEE_RATIO = 20_000_000n

/**
 * Days to maturity, **ceilinged to whole days** — exactly as
 * `TermMaxOrderV2._daysToMaturity` does it:
 *
 *   `(maturity - now + SECONDS_IN_DAY - 1) / SECONDS_IN_DAY`
 *
 * Reproduce the ceiling or an off-chain quote drifts by up to a full day of
 * interest against the contract. Returns 0 at/after maturity.
 */
export function daysToMaturity(maturity: number, nowSec: number): bigint {
  const remaining = BigInt(Math.trunc(maturity)) - BigInt(Math.trunc(nowSec))
  if (remaining <= 0n) return 0n
  return (remaining + SECONDS_IN_DAY - 1n) / SECONDS_IN_DAY
}

/**
 * Pick the active curve segment for a given XT reserve — `TermMaxCurve.calcCutId`.
 * Segments are ordered by their left edge; the active one is the LAST whose
 * `xtReserve <= reserve`. Returns -1 for an empty curve.
 */
export function calcCutId(
  cuts: TermMaxCurveSegment[],
  xtReserve: bigint,
): number {
  let id = cuts.length
  while (id > 0) {
    id--
    if (xtReserve >= cuts[id].xtReserve) return id
  }
  return cuts.length > 0 ? 0 : -1
}

/**
 * Local virtual reserves for one segment — `TermMaxCurve.calcIntervalProps`.
 *
 * `netInterestFactor` (nif) folds the taker fee into the curve:
 *   taker LENDS   → `1e8 - lendTakerFeeRatio`
 *   taker BORROWS → `1e8 + borrowTakerFeeRatio`
 * Pass `DECIMAL_BASE` for the fee-free mid rate (what `apr()` reports).
 */
export function intervalProps(
  netInterestFactor: bigint,
  days: bigint,
  cut: TermMaxCurveSegment,
  xtReserve: bigint,
): { liqSquare: bigint; vXtReserve: bigint; vFtReserve: bigint } | null {
  if (days <= 0n) return null
  const liqSquare =
    (cut.liqSquare * days * netInterestFactor) / (DAYS_IN_YEAR * DECIMAL_BASE)
  const vXtReserve = xtReserve + cut.offset
  if (vXtReserve <= 0n) return null
  return { liqSquare, vXtReserve, vFtReserve: liqSquare / vXtReserve }
}

/**
 * Marginal (fee-free) APR implied by a curve at a given reserve, 1e8-scaled —
 * the same formula `TermMaxOrderV2.apr()` uses:
 *
 *   `apr = vFt · 1e8 · 365 / (vXt · daysToMaturity)`
 *
 * Returns 0n for an empty curve or a matured market. This is a MID PRICE at the
 * current reserve: it ignores both the taker fee and trade size, so it is for
 * display and sorting only — never quote against it.
 */
export function curveApr(
  cuts: TermMaxCurveSegment[],
  virtualXtReserve: bigint,
  days: bigint,
): bigint {
  if (cuts.length === 0 || days <= 0n) return 0n
  const id = calcCutId(cuts, virtualXtReserve)
  if (id < 0) return 0n
  const props = intervalProps(DECIMAL_BASE, days, cuts[id], virtualXtReserve)
  if (!props || props.vXtReserve === 0n) return 0n
  return (props.vFtReserve * DECIMAL_BASE * DAYS_IN_YEAR) / (props.vXtReserve * days)
}

/** 1e8-scaled ratio → plain fraction (`5_500_000n` → `0.055`). */
export function ratioToNumber(v: bigint): number {
  return Number(v) / Number(DECIMAL_BASE)
}

/** 1e8-scaled ratio → percent (`5_500_000n` → `5.5`). */
export function ratioToPercent(v: bigint): number {
  return (Number(v) / Number(DECIMAL_BASE)) * 100
}

/**
 * Marginal APR as a fraction for one side, taker-side by construction.
 *
 * Pass `takerLendCuts` for the lend rate and `takerBorrowCuts` for the borrow
 * rate — the maker/taker crossing has already been applied when those fields
 * were built (see types.ts).
 */
export function curveAprNumber(
  cuts: TermMaxCurveSegment[],
  virtualXtReserve: bigint,
  days: bigint,
): number {
  return ratioToNumber(curveApr(cuts, virtualXtReserve, days))
}

/**
 * Net-interest factor for a taker LEND (`1e8 - lendTakerFeeRatio`).
 * Guarded so a malformed fee config cannot produce a non-positive factor.
 */
export function lendNif(fee?: TermMaxFeeConfig): bigint {
  const f = fee?.lendTakerFeeRatio ?? 0n
  const nif = DECIMAL_BASE - (f > MAX_FEE_RATIO ? MAX_FEE_RATIO : f)
  return nif > 0n ? nif : DECIMAL_BASE
}

/** Net-interest factor for a taker BORROW (`1e8 + borrowTakerFeeRatio`). */
export function borrowNif(fee?: TermMaxFeeConfig): bigint {
  const f = fee?.borrowTakerFeeRatio ?? 0n
  return DECIMAL_BASE + (f > MAX_FEE_RATIO ? MAX_FEE_RATIO : f)
}

/**
 * The GT-mint fee ratio at a given time to maturity, 1e8-scaled — mirrors
 * `TermMaxMarketV2.mintGtFeeRatio()`:
 *
 *   `days · feeRatio · feeRef / (365·1e8 + feeRef·days)`
 *
 * This is a genuine percentage OF PRINCIPAL charged once at borrow time
 * (`issueFee = debt · ratio / 1e8`), so it maps onto the cross-protocol
 * `FixedTermInfo.fees.originationFeePercent`. It is NOT the raw
 * `feeConfig.mintGtFeeRatio` — prefer reading the contract when you can.
 */
export function mintGtFeeRatio(
  fee: TermMaxFeeConfig | undefined,
  days: bigint,
): bigint {
  if (!fee || days <= 0n) return 0n
  const ratio = fee.mintGtFeeRatio ?? 0n
  const ref = fee.mintGtFeeRef ?? 0n
  const denom = DAYS_IN_YEAR * DECIMAL_BASE + ref * days
  if (denom === 0n) return 0n
  return (days * ratio * ref) / denom
}

/**
 * TermMax liquidation penalty as a fraction of the repaid debt.
 *
 * Protocol constants, not per-market: the liquidator is paid 5% and the
 * protocol reserve takes 5%, so the borrower loses 10% of the liquidated debt
 * value. (Loans over $10k can only be liquidated 50% at a time; that is a size
 * cap, not a penalty, and is modeled as `closeFactor`.)
 */
export const TERMMAX_LIQUIDATION_PENALTY = 0.1
export const TERMMAX_LIQUIDATOR_BONUS = 0.05
/** Partial-liquidation threshold, USD: above it a single call can take at most 50%. */
export const TERMMAX_PARTIAL_LIQUIDATION_THRESHOLD_USD = 10_000
export const TERMMAX_PARTIAL_CLOSE_FACTOR = 0.5

/** Post-maturity liquidation window before physical delivery (`Constants.LIQUIDATION_WINDOW`). */
export const TERMMAX_LIQUIDATION_WINDOW_SECS = 7200

/**
 * Parse a 1e8-scaled LTV string into a plain fraction (`87500000` → `0.875`).
 * Returns 0 for missing/garbage input rather than NaN, so a bad config row
 * degrades to "no collateral value" instead of poisoning the optimizer.
 */
export function parseTermMaxLtv(v: string | number | bigint | undefined): number {
  if (v === undefined || v === null || v === '') return 0
  try {
    return Number(BigInt(v)) / Number(DECIMAL_BASE)
  } catch {
    const n = Number(v)
    return Number.isFinite(n) ? n / Number(DECIMAL_BASE) : 0
  }
}

/**
 * Convert a 1e8-scaled TermMax LTV to the WAD (1e18) scale used by
 * `params.market.lltv`.
 *
 * TermMax stores LTVs at 1e8 (`91500000` = 91.5%), but `params.market.lltv` on
 * the shared Morpho response shape is WAD for Morpho and Midnight — and
 * downstream (`morpho_markets.lltv`) is already a mixed column, so emitting the
 * raw 1e8 value would be read as ~9.15e-11 by anything dividing by 1e18. The
 * TermMax-native 1e8 values stay available untouched on `params.market.maxLtv`
 * / `liquidationLtv`.
 */
export function termMaxLtvToWad(v: string | number | bigint | undefined): string {
  if (v === undefined || v === null || v === '') return '0'
  try {
    // 1e8 → 1e18 is a ×1e10 shift; done in bigint so no precision is lost.
    return (BigInt(v) * 10_000_000_000n).toString()
  } catch {
    return '0'
  }
}
